import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiCreateWorkspaceTopic, type WorkspaceTopicItem } from "../lib/authApi";
import { useWorkspaceTopics } from "../hooks/useWorkspaceTopics";
import { topicAvatarGlyph } from "../lib/topicAvatarGlyph";
import { getLastTopicIdForWorkspace } from "../lib/workspaceTopicMemory";
import { useLongPress, type LongPressCoords } from "../hooks/useLongPress";
import { AppIcon } from "./AppIcon";
import { SidebarContextMenu } from "./SidebarContextMenu";

export type WorkspaceTopicSelectArgs = {
  workspaceId: string;
  topicId: string;
  workspaceName: string;
  topicName: string;
};

type WorkspaceTopicListProps = {
  enabled: boolean;
  workspaceId: string;
  workspaceName: string;
  canCreateTopics?: boolean;
  autoSelectFirst?: boolean;
  variant?: "rail" | "mobile-screen";
  selectedTopicKey?: { workspaceId: string; topicId: string } | null;
  onSelectTopic?: (args: WorkspaceTopicSelectArgs) => void;
  topicsRefreshEpoch?: number;
  currentUserId?: string;
  resolveAuthorName?: (authorId: string) => string | null;
  /** Increment to open create-form from outside (mobile header button). */
  createTrigger?: number;
  /** Owner/admin: shared topic pin in sidebar. */
  workspaceRole?: "owner" | "admin" | "member";
  onPinnedTopicIdsChange?: (workspaceId: string, ids: string[]) => void;
};

function formatTopicTimestamp(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);
  if (dayDiff >= 1 && dayDiff < 7) {
    return date.toLocaleDateString("ru-RU", { weekday: "short" });
  }

  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

export function WorkspaceTopicList({
  enabled,
  workspaceId,
  workspaceName,
  canCreateTopics,
  autoSelectFirst = true,
  variant = "rail",
  selectedTopicKey,
  onSelectTopic,
  topicsRefreshEpoch = 0,
  currentUserId,
  resolveAuthorName,
  createTrigger = 0,
  workspaceRole = "member",
  onPinnedTopicIdsChange,
}: WorkspaceTopicListProps) {
  // Primitive (string | null) derived from selectedTopicKey so useMemo deps compare by value,
  // not object reference. Represents the topicId currently open in THIS workspace (if any).
  const selectedTopicIdForThisWorkspace =
    selectedTopicKey?.workspaceId === workspaceId ? selectedTopicKey.topicId : null;

  // Priority: currently open topic in this workspace > last remembered topic from storage.
  // Keeping ensureTopicId in sync with the open topic ensures every background epoch refetch
  // asks the server to include that topic even when it lives outside the first page.
  // deps use primitives so memo only re-runs when the actual topicId changes.
  const ensureTopicId = useMemo(
    () => selectedTopicIdForThisWorkspace ?? getLastTopicIdForWorkspace(workspaceId),
    [workspaceId, selectedTopicIdForThisWorkspace],
  );
  const {
    topics,
    pinnedTopicIds,
    initialLoading,
    refreshing,
    loadingMore,
    error,
    hasMore,
    loadMore,
    addLocalTopic,
    togglePinnedTopic,
  } = useWorkspaceTopics({
    enabled,
    workspaceId,
    topicsRefreshEpoch,
    ensureTopicId,
    onPinnedTopicIdsChange,
  });

  const pinOrder = useMemo(() => new Map(pinnedTopicIds.map((id, i) => [id, i])), [pinnedTopicIds]);

  const sortedTopics = useMemo(() => {
    const list = [...topics];
    list.sort((a, b) => {
      const ap = pinOrder.has(a.id) ? 1 : 0;
      const bp = pinOrder.has(b.id) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if (ap && bp) return (pinOrder.get(a.id) ?? 0) - (pinOrder.get(b.id) ?? 0);
      const ta = a.created_at - b.created_at;
      if (ta !== 0) return ta;
      return a.id.localeCompare(b.id);
    });
    return list;
  }, [topics, pinOrder]);

  const canPinTopics = workspaceRole === "owner" || workspaceRole === "admin";

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; topicId: string } | null>(null);
  const onLongPressTopic = useCallback(
    (topicId: string) => (coords: LongPressCoords) => {
      setCtxMenu({ ...coords, topicId });
    },
    [],
  );

  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creating) createInputRef.current?.focus();
  }, [creating]);

  useEffect(() => {
    if (createTrigger > 0) setCreating(true);
  }, [createTrigger]);

  useEffect(() => {
    setCreating(false);
    setCreateName("");
    setCreateError(null);
  }, [workspaceId]);

  /** Десктоп: при выборе workspace в сайдбаре сразу открыть последний topic или первый. */
  useEffect(() => {
    if (!autoSelectFirst) return;
    if (!enabled || !onSelectTopic || initialLoading || error || topics.length === 0) return;

    const key = selectedTopicKey;
    const viewingThisWorkspace =
      key?.workspaceId === workspaceId && topics.some((t) => t.id === key.topicId);
    if (viewingThisWorkspace) return;

    // selectedTopicKey for this workspace but topic not in `topics` yet: wait while
    // the list refetches (ensureTopicId / epoch). After load, if still missing
    // (deleted topic, etc.) fall through to remembered or first — otherwise we never open a chat.
    if (key?.workspaceId === workspaceId && !topics.some((t) => t.id === key.topicId)) {
      if (refreshing) return;
    }

    const rememberedId = getLastTopicIdForWorkspace(workspaceId);
    const chosen =
      (rememberedId ? topics.find((t) => t.id === rememberedId) : undefined) ?? topics[0]!;
    onSelectTopic({
      workspaceId,
      topicId: chosen.id,
      workspaceName,
      topicName: chosen.name,
    });
  }, [
    enabled,
    workspaceId,
    workspaceName,
    initialLoading,
    refreshing,
    error,
    topics,
    selectedTopicKey,
    onSelectTopic,
    autoSelectFirst,
  ]);

  const submitCreate = useCallback(async () => {
    const name = createName.trim();
    if (!name || createBusy) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const { topic } = await apiCreateWorkspaceTopic(workspaceId, name);
      addLocalTopic(topic);
      setCreating(false);
      setCreateName("");
      onSelectTopic?.({ workspaceId, topicId: topic.id, workspaceName, topicName: topic.name });
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreateBusy(false);
    }
  }, [createName, createBusy, workspaceId, workspaceName, addLocalTopic, onSelectTopic]);

  if (!enabled) return null;

  const isMobileScreen = variant === "mobile-screen";

  return (
    <div className={`workspace-topic-list workspace-topic-list--${variant}`}>
      {canCreateTopics && (!isMobileScreen || creating) ? (
        <div className="workspace-nav__topic-create-area">
          {creating ? (
            <form
              className="workspace-nav__create-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCreate();
              }}
            >
              <input
                ref={createInputRef}
                className="workspace-nav__create-input"
                type="text"
                placeholder={isMobileScreen ? "имя темы…" : "имя topic…"}
                maxLength={128}
                value={createName}
                disabled={createBusy}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setCreating(false);
                    setCreateName("");
                    setCreateError(null);
                  }
                }}
              />
              <button type="submit" className="workspace-nav__create-ok" disabled={!createName.trim() || createBusy}>
                OK
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="workspace-nav__add-topic-btn"
              title={isMobileScreen ? "Создать тему" : "Создать topic"}
              onClick={() => setCreating(true)}
            >
              <AppIcon name="plus" className="workspace-nav__add-icon" size={14} />
              <span>{isMobileScreen ? "тема" : "topic"}</span>
            </button>
          )}
          {createError ? <p className="workspace-nav__err">{createError}</p> : null}
        </div>
      ) : null}
      {error ? <p className="workspace-nav__err">{error}</p> : null}
      {initialLoading && topics.length === 0 ? (
        <p className="workspace-nav__hint">загрузка…</p>
      ) : !error && topics.length === 0 ? (
        <p className="workspace-nav__hint">
          {isMobileScreen
            ? canCreateTopics
              ? "Тем пока нет. Создайте первую тему."
              : "Темы пока не созданы"
            : canCreateTopics
              ? "создайте первый topic"
              : "в этом workspace пока нет topics"}
        </p>
      ) : topics.length > 0 ? (
        <>
        <ul className="contact-list workspace-nav__list">
          {sortedTopics.map((t) => (
            <TopicRow
              key={t.id}
              topic={t}
              workspaceId={workspaceId}
              workspaceName={workspaceName}
              isMobileScreen={isMobileScreen}
              isSelected={selectedTopicKey?.workspaceId === workspaceId && selectedTopicKey.topicId === t.id}
              pinned={pinnedTopicIds.includes(t.id)}
              canPinTopics={canPinTopics}
              currentUserId={currentUserId}
              resolveAuthorName={resolveAuthorName}
              onSelectTopic={onSelectTopic}
              onLongPress={onLongPressTopic(t.id)}
            />
          ))}
        </ul>
        {ctxMenu && canPinTopics ? (
          <SidebarContextMenu
            x={ctxMenu.x}
            y={ctxMenu.y}
            mode="shared-topic"
            sharedTopic={{
              isPinned: pinnedTopicIds.includes(ctxMenu.topicId),
              onToggle: () => {
                const pin = !pinnedTopicIds.includes(ctxMenu.topicId);
                void togglePinnedTopic(ctxMenu.topicId, pin).catch(() => undefined);
              },
            }}
            onClose={() => setCtxMenu(null)}
          />
        ) : null}
        {hasMore ? (
          <div className="workspace-nav__topic-load-more">
            <button
              type="button"
              className="workspace-nav__load-more-topics"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? "загрузка…" : isMobileScreen ? "ещё темы" : "ещё topics"}
            </button>
          </div>
        ) : null}
        </>
      ) : null}
    </div>
  );
}

function TopicRow({
  topic: t,
  workspaceId,
  workspaceName,
  isMobileScreen,
  isSelected,
  pinned,
  canPinTopics,
  currentUserId,
  resolveAuthorName,
  onSelectTopic,
  onLongPress,
}: {
  topic: WorkspaceTopicItem;
  workspaceId: string;
  workspaceName: string;
  isMobileScreen: boolean;
  isSelected: boolean;
  pinned: boolean;
  canPinTopics: boolean;
  currentUserId?: string;
  resolveAuthorName?: (authorId: string) => string | null;
  onSelectTopic?: (args: WorkspaceTopicSelectArgs) => void;
  onLongPress: (coords: LongPressCoords) => void;
}) {
  const noopLongPress = useCallback(() => () => {}, []);
  const lp = useLongPress(canPinTopics ? onLongPress : noopLongPress);
  const initial = topicAvatarGlyph(t.name);
  const stamp = t.last_message_at ?? t.created_at;
  const preview = t.last_message_preview?.trim() ? t.last_message_preview : "Нет сообщений";
  const resolvedAuthorName =
    t.last_message_author_id && resolveAuthorName ? resolveAuthorName(t.last_message_author_id) : null;
  const authorPrefix =
    t.last_message_preview && t.last_message_author_id
      ? currentUserId && t.last_message_author_id === currentUserId
        ? "Вы: "
        : resolvedAuthorName
          ? `${resolvedAuthorName}: `
          : ""
      : "";
  return (
    <li>
      <div
        role={onSelectTopic ? "button" : undefined}
        tabIndex={onSelectTopic ? 0 : undefined}
        className={`contact-row workspace-topic-row ${isMobileScreen ? "workspace-topic-row--mobile" : ""} ${onSelectTopic ? "" : "contact-row--static"} ${isSelected ? "is-selected" : ""}`}
        title={onSelectTopic ? `Открыть topic «${t.name}»` : undefined}
        onClick={
          onSelectTopic
            ? () => onSelectTopic({ workspaceId, topicId: t.id, workspaceName, topicName: t.name })
            : undefined
        }
        onKeyDown={
          onSelectTopic
            ? (e) => {
                if (e.key === "Enter") onSelectTopic({ workspaceId, topicId: t.id, workspaceName, topicName: t.name });
              }
            : undefined
        }
        {...lp}
      >
        <div className="contact-avatar">
          <span className="contact-avatar__letter">{initial}</span>
        </div>
        <div className="contact-row__info workspace-nav__topic-info">
          <span className="workspace-topic-row__top">
            <span className="contact-row__name workspace-nav__topic-name-row">
              {pinned ? <AppIcon name="pin" size={11} className="contact-row__pin-icon" /> : null}
              <span className="workspace-nav__topic-name-text">{t.name}</span>
            </span>
            <span className="workspace-topic-row__time" aria-label="Последняя активность">
              {formatTopicTimestamp(stamp)}
            </span>
          </span>
          <span
            className={`workspace-nav__topic-preview ${!t.last_message_preview ? "workspace-nav__topic-preview--empty" : ""}`}
            title={preview}
          >
            {authorPrefix}
            {preview}
          </span>
        </div>
        {t.unread_count > 0 ? (
          <div className="contact-row__tail">
            <span className="contact-row__badge workspace-nav__topic-badge">{t.unread_count}</span>
          </div>
        ) : null}
      </div>
    </li>
  );
}
